import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Banner, Box, Button, Dialog, Heading, Separator, Text, Tooltip } from '@betterment/samba-react';

import { TaxImpact } from './data';

function formatGainsOrLoss(moneyCents: number) {
  if (moneyCents > 0) {
    return formatMoney(toMoney(moneyCents), 'credit');
  }
  return formatMoney(toMoney(moneyCents));
}

export function formatEstimatedTaxes({ netEstimatedTaxesCents }: TaxImpact): string {
  return formatMoney(toMoney(Math.abs(netEstimatedTaxesCents)));
}

const TaxImpactMessage: React.FC<React.PropsWithChildren<TaxImpact>> = (taxImpact: TaxImpact) => {
  const { netEstimatedTaxesCents } = taxImpact;
  const estimatedTaxes = formatEstimatedTaxes(taxImpact);
  const { estimatedTaxImpactBannerCopy: message } = taxImpact;

  return (
    <>
      <Text as="p" id="estimatedTaxImpact" css={{ fontWeight: 'bold', mb: 'space.200' }}>
        Estimated taxes you'll {netEstimatedTaxesCents < 0 ? 'save' : 'owe'}: {estimatedTaxes}
      </Text>
      <Text as="p" css={{ mb: 'space.200' }}>
        {message}
      </Text>
    </>
  );
};

const HeaderRow: React.FC<{ name: string; label: string; value: string; tooltip: string }> = ({
  name,
  label,
  value,
  tooltip,
}) => (
  <Box css={{ display: 'flex', justifyContent: 'space-between', mb: 'space.50' }}>
    <Box css={{ display: 'flex' }}>
      <Text css={{ fontWeight: 'bold', mr: 'space.25' }} id={name}>
        {label}
      </Text>
      <Tooltip>
        <Tooltip.Trigger asChild={true}>
          <Tooltip.IconButton />
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Text>{tooltip}</Tooltip.Text>
        </Tooltip.Content>
      </Tooltip>
    </Box>
    <Box>
      <Text aria-labelledby={name}>{value}</Text>
    </Box>
  </Box>
);

const Section: React.FC<React.PropsWithChildren> = ({ children }) => <Box css={{ mb: 'space.400' }}>{children}</Box>;

const Row: React.FC<{ name: string; label: string; value: string }> = ({ name, label, value }) => (
  <Box css={{ display: 'flex', justifyContent: 'space-between', mb: 'space.50' }}>
    <Box>
      <Text css={{ color: 'primitives.grey.50', mr: 'space.25' }} id={name}>
        {label}
      </Text>
    </Box>
    <Box>
      <Text css={{ color: 'primitives.grey.50' }} aria-labelledby={name}>
        {value}
      </Text>
    </Box>
  </Box>
);

export const TaxImpactDialogContent: React.FC<TaxImpact> = props => {
  const {
    howWeEstimateTaxImpactCopy,
    netLongTermCents,
    longTermGainCents,
    longTermLossCents,
    longTermGainEstimationCopy,
    shortTermGainEstimationCopy,
    longTermPermanentlyWashedCents,
    shortTermGainCents,
    shortTermLossCents,
    netWashedLossesCents,
    netShortTermCents,
    netEstimatedTaxesCents,
    washCopy,
  } = props;

  return (
    <Dialog.Content css={{ p: 'space.400' }} size="large">
      <Dialog.Title asChild={true}>
        <Heading variant="title300" css={{ mb: 'space.300' }} tabIndex={0}>
          Estimated tax impact
        </Heading>
      </Dialog.Title>
      <Section>
        <HeaderRow
          name="netLongTerm"
          label="Net long-term gains or loss"
          tooltip={longTermGainEstimationCopy}
          value={formatGainsOrLoss(netLongTermCents)}
        />
        <Row name="longTermGain" label="Long-term gains" value={formatGainsOrLoss(longTermGainCents)} />
        <Row name="longTermLoss" label="Long-term loss" value={formatMoney(toMoney(longTermLossCents), 'debit')} />
      </Section>
      <Section>
        <HeaderRow
          name="netShortTerm"
          label="Net short-term gains or loss"
          tooltip={shortTermGainEstimationCopy}
          value={formatGainsOrLoss(netShortTermCents)}
        />
        <Row name="shortTermGain" label="Short-term gains" value={formatGainsOrLoss(shortTermGainCents)} />
        <Row name="shortTermLoss" label="Short-term loss" value={formatMoney(toMoney(shortTermLossCents), 'debit')} />
      </Section>
      <Section>
        <HeaderRow
          name="netWashedLoss"
          label="Washed loss"
          tooltip={washCopy}
          value={formatMoney(toMoney(netWashedLossesCents), 'debit')}
        />
        <Row
          name="permanentlyWashedLoss"
          label="Permanently washed loss"
          value={formatMoney(toMoney(longTermPermanentlyWashedCents), 'debit')}
        />
      </Section>
      <Separator css={{ marginY: 'space.200' }} />
      <Section>
        <Box css={{ display: 'flex', justifyContent: 'space-between', mb: 'space.50' }}>
          <Text css={{ fontWeight: 'bold' }} id="estimatedTaxImpact">
            Estimated tax impact
          </Text>
          <Text css={{ fontWeight: 'bold' }} aria-labelledby="estimatedTaxImpact">
            {formatMoney(toMoney(netEstimatedTaxesCents))}
          </Text>
        </Box>
      </Section>
      {howWeEstimateTaxImpactCopy.split('\n').map((str, key) => {
        return (
          <Text
            as="p"
            css={{ fontSize: 'fontSize.25', mb: 'space.100', color: 'primitives.grey.50', textAlign: 'left' }}
            // eslint-disable-next-line react/no-array-index-key
            key={key}
          >
            {str}
          </Text>
        );
      })}
      <Box css={{ marginTop: 'space.200' }}>
        <Dialog.Close asChild={true}>
          <Button variant="solidSecondary" css={{ display: 'inline' }}>
            Close
          </Button>
        </Dialog.Close>
      </Box>
    </Dialog.Content>
  );
};

const EstimatedTaxImpactBanner: React.FC<React.PropsWithChildren<TaxImpact>> = props => {
  const { estimatedTaxImpactDisclosureCopy } = props;

  return (
    <>
      <Banner variant="info">
        <Banner.Content>
          <TaxImpactMessage {...props} />
          <Dialog>
            <Dialog.Trigger asChild={true}>
              <Button type="button" variant="textPrimary" aria-label="Learn more about how we got this number">
                Learn more about how we got this number
              </Button>
            </Dialog.Trigger>
            <TaxImpactDialogContent {...props} />
          </Dialog>
          <Text as="div" css={{ color: 'primitives.grey.50', fontSize: 'fontSize.25', mt: 'space.300' }}>
            {estimatedTaxImpactDisclosureCopy}
          </Text>
        </Banner.Content>
      </Banner>
    </>
  );
};

export default EstimatedTaxImpactBanner;
